/**
 * Human Coaching API
 *
 * RTK Query endpoints for human coaching operations (coach profiles, engagements, bookings).
 */

import { api } from '../api';

// ============================================
// Types
// ============================================

export type EngagementType = 'one_time' | 'subscription' | 'package';
export type EngagementStatus = 'pending' | 'active' | 'paused' | 'completed' | 'cancelled';
export type BookingStatus = 'requested' | 'scheduled' | 'completed' | 'cancelled';

export interface CoachProfile {
  id: string;
  userId: string;
  headline: string;
  bio?: string;
  specialties: string[];
  rateOneTime?: number;
  rateMonthly?: number;
  sessionLengthMinutes: number;
  maxClients?: number;
  isAcceptingClients: boolean;
  user?: {
    id: string;
    firstName?: string;
    lastName?: string;
    email?: string;
  };
}

export interface EngagementParty {
  id: string;
  firstName?: string;
  lastName?: string;
}

export interface CoachingEngagement {
  id: string;
  coachUserId: string;
  clientUserId: string;
  orgId?: string;
  type: EngagementType;
  status: EngagementStatus;
  planLabel: string;
  sessionLengthMinutes: number;
  maxSessionsPerMonth?: number;
  autoShareSessions: boolean;
  stripeSubscriptionId?: string;
  startedAt?: string;
  endedAt?: string;
  createdAt: string;
  /** The other party: name only — the API never returns email addresses here. */
  coach?: EngagementParty;
  client?: EngagementParty;
  organization?: {
    id: string;
    name: string;
  };
}

export interface CoachingBooking {
  id: string;
  engagementId: string;
  coachUserId: string;
  clientUserId: string;
  orgId?: string;
  status: BookingStatus;
  requestedByUserId: string;
  requestedTime?: string;
  scheduledTime?: string;
  durationMinutes: number;
  notes?: string;
  practiceSessionId?: string;
  completedAt?: string;
  cancelledAt?: string;
  cancelledByUserId?: string;
  createdAt: string;
  /** The other party: name only — the API never returns email addresses here. */
  coach?: EngagementParty;
  client?: EngagementParty;
  engagement?: {
    id: string;
    planLabel: string;
    type: EngagementType;
  };
}

export interface SharedPracticeSession {
  id: string;
  userId: string;
  scenarioId?: string;
  status: string;
  coachType: string;
  finalScore?: number;
  endedAt?: string;
  user?: {
    id: string;
    firstName?: string;
    lastName?: string;
  };
}

export interface Pagination {
  total: number;
  limit: number;
  offset: number;
  hasMore: boolean;
}

// Request types
export interface UpdateCoachProfileRequest {
  headline?: string;
  bio?: string;
  specialties?: string[];
  rateOneTime?: number;
  rateMonthly?: number;
  sessionLengthMinutes?: number;
  maxClients?: number;
  isAcceptingClients?: boolean;
}

export interface CreateEngagementRequest {
  coachUserId: string;
  type: EngagementType;
  planLabel: string;
  orgId?: string;
  sessionLengthMinutes?: number;
  maxSessionsPerMonth?: number;
  autoShareSessions?: boolean;
}

export interface RequestBookingRequest {
  engagementId: string;
  requestedTime?: string;
  durationMinutes?: number;
  notes?: string;
}

export interface ScheduleBookingRequest {
  scheduledTime: string;
}

export interface CompleteBookingRequest {
  practiceSessionId?: string;
}

// Response wrappers
interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
}

// ============================================
// API Definition
// ============================================

export const humanCoachingApi = api.injectEndpoints({
  endpoints: (builder) => ({
    // ============================================
    // Coach Profile Endpoints
    // ============================================

    /**
     * Get current user's coach profile
     */
    getMyCoachProfile: builder.query<CoachProfile, void>({
      query: () => '/coaches/profile',
      transformResponse: (response: ApiResponse<CoachProfile>) => response.data,
      providesTags: ['CoachProfile'],
    }),

    /**
     * Update current user's coach profile
     */
    updateMyCoachProfile: builder.mutation<CoachProfile, UpdateCoachProfileRequest>({
      query: (body) => ({
        url: '/coaches/profile',
        method: 'PUT',
        body,
      }),
      transformResponse: (response: ApiResponse<CoachProfile>) => response.data,
      invalidatesTags: ['CoachProfile'],
    }),

    /**
     * List featured coaches
     */
    getFeaturedCoaches: builder.query<
      { coaches: CoachProfile[]; pagination: Pagination },
      { specialty?: string; limit?: number; offset?: number }
    >({
      query: ({ specialty, limit = 20, offset = 0 }) => {
        const params = new URLSearchParams();
        if (specialty) params.append('specialty', specialty);
        params.append('limit', String(limit));
        params.append('offset', String(offset));
        return `/coaches/featured?${params.toString()}`;
      },
      transformResponse: (response: ApiResponse<{ coaches: CoachProfile[]; pagination: Pagination }>) =>
        response.data,
    }),

    // ============================================
    // Engagement Endpoints
    // ============================================

    /**
     * Get engagements where current user is the coach
     */
    getEngagementsAsCoach: builder.query<
      { engagements: CoachingEngagement[]; pagination: Pagination },
      { status?: EngagementStatus[]; limit?: number; offset?: number }
    >({
      query: ({ status, limit = 50, offset = 0 }) => {
        const params = new URLSearchParams();
        if (status?.length) params.append('status', status.join(','));
        params.append('limit', String(limit));
        params.append('offset', String(offset));
        return `/human-coaching/engagements/me/as-coach?${params.toString()}`;
      },
      transformResponse: (response: ApiResponse<{ engagements: CoachingEngagement[]; pagination: Pagination }>) =>
        response.data,
      providesTags: ['Engagements'],
    }),

    /**
     * Get engagements where current user is the client
     */
    getEngagementsAsClient: builder.query<
      { engagements: CoachingEngagement[]; pagination: Pagination },
      { status?: EngagementStatus[]; limit?: number; offset?: number }
    >({
      query: ({ status, limit = 50, offset = 0 }) => {
        const params = new URLSearchParams();
        if (status?.length) params.append('status', status.join(','));
        params.append('limit', String(limit));
        params.append('offset', String(offset));
        return `/human-coaching/engagements/me/as-client?${params.toString()}`;
      },
      transformResponse: (response: ApiResponse<{ engagements: CoachingEngagement[]; pagination: Pagination }>) =>
        response.data,
      providesTags: ['Engagements'],
    }),

    /**
     * Create a new coaching engagement
     */
    createEngagement: builder.mutation<CoachingEngagement, CreateEngagementRequest>({
      query: (body) => ({
        url: '/human-coaching/engagements',
        method: 'POST',
        body,
      }),
      transformResponse: (response: ApiResponse<CoachingEngagement>) => response.data,
      invalidatesTags: ['Engagements'],
    }),

    /**
     * Cancel an engagement
     */
    cancelEngagement: builder.mutation<CoachingEngagement, string>({
      query: (id) => ({
        url: `/human-coaching/engagements/${id}/cancel`,
        method: 'POST',
      }),
      transformResponse: (response: ApiResponse<CoachingEngagement>) => response.data,
      invalidatesTags: ['Engagements'],
    }),

    /**
     * Toggle auto-share sessions
     */
    toggleAutoShare: builder.mutation<CoachingEngagement, { id: string; autoShare: boolean }>({
      query: ({ id, autoShare }) => ({
        url: `/human-coaching/engagements/${id}/auto-share`,
        method: 'PUT',
        body: { autoShare },
      }),
      transformResponse: (response: ApiResponse<CoachingEngagement>) => response.data,
      invalidatesTags: ['Engagements'],
    }),

    /**
     * Get shared practice sessions for coach
     */
    getSharedSessions: builder.query<
      { sessions: SharedPracticeSession[]; pagination: Pagination },
      { limit?: number; offset?: number }
    >({
      query: ({ limit = 20, offset = 0 }) => {
        const params = new URLSearchParams();
        params.append('limit', String(limit));
        params.append('offset', String(offset));
        return `/human-coaching/shared-sessions?${params.toString()}`;
      },
      transformResponse: (response: ApiResponse<{ sessions: SharedPracticeSession[]; pagination: Pagination }>) =>
        response.data,
      providesTags: ['SharedSessions'],
    }),

    // ============================================
    // Booking Endpoints
    // ============================================

    /**
     * Get bookings for current user
     */
    getBookings: builder.query<
      { bookings: CoachingBooking[]; pagination: Pagination },
      { asCoach?: boolean; status?: BookingStatus[]; limit?: number; offset?: number }
    >({
      query: ({ asCoach = false, status, limit = 50, offset = 0 }) => {
        const params = new URLSearchParams();
        params.append('asCoach', String(asCoach));
        if (status?.length) params.append('status', status.join(','));
        params.append('limit', String(limit));
        params.append('offset', String(offset));
        return `/human-coaching/bookings?${params.toString()}`;
      },
      transformResponse: (response: ApiResponse<{ bookings: CoachingBooking[]; pagination: Pagination }>) =>
        response.data,
      providesTags: ['Bookings'],
    }),

    /**
     * Get upcoming bookings
     */
    getUpcomingBookings: builder.query<
      { bookings: CoachingBooking[] },
      { asCoach?: boolean; limit?: number }
    >({
      query: ({ asCoach = false, limit = 10 }) => {
        const params = new URLSearchParams();
        params.append('asCoach', String(asCoach));
        params.append('limit', String(limit));
        return `/human-coaching/bookings/upcoming?${params.toString()}`;
      },
      transformResponse: (response: ApiResponse<{ bookings: CoachingBooking[] }>) => response.data,
      providesTags: ['Bookings'],
    }),

    /**
     * Request a new coaching session
     */
    requestBooking: builder.mutation<CoachingBooking, RequestBookingRequest>({
      query: (body) => ({
        url: '/human-coaching/bookings',
        method: 'POST',
        body,
      }),
      transformResponse: (response: ApiResponse<CoachingBooking>) => response.data,
      invalidatesTags: ['Bookings'],
    }),

    /**
     * Schedule a booking (coach only)
     */
    scheduleBooking: builder.mutation<CoachingBooking, { id: string; scheduledTime: string }>({
      query: ({ id, scheduledTime }) => ({
        url: `/human-coaching/bookings/${id}/schedule`,
        method: 'POST',
        body: { scheduledTime },
      }),
      transformResponse: (response: ApiResponse<CoachingBooking>) => response.data,
      invalidatesTags: ['Bookings'],
    }),

    /**
     * Complete a booking
     */
    completeBooking: builder.mutation<CoachingBooking, { id: string; practiceSessionId?: string }>({
      query: ({ id, practiceSessionId }) => ({
        url: `/human-coaching/bookings/${id}/complete`,
        method: 'POST',
        body: { practiceSessionId },
      }),
      transformResponse: (response: ApiResponse<CoachingBooking>) => response.data,
      invalidatesTags: ['Bookings'],
    }),

    /**
     * Cancel a booking
     */
    cancelBooking: builder.mutation<CoachingBooking, string>({
      query: (id) => ({
        url: `/human-coaching/bookings/${id}/cancel`,
        method: 'POST',
      }),
      transformResponse: (response: ApiResponse<CoachingBooking>) => response.data,
      invalidatesTags: ['Bookings'],
    }),
  }),
  overrideExisting: false,
});

// Export hooks
export const {
  // Coach Profile
  useGetMyCoachProfileQuery,
  useUpdateMyCoachProfileMutation,
  useGetFeaturedCoachesQuery,
  // Engagements
  useGetEngagementsAsCoachQuery,
  useGetEngagementsAsClientQuery,
  useCreateEngagementMutation,
  useCancelEngagementMutation,
  useToggleAutoShareMutation,
  useGetSharedSessionsQuery,
  // Bookings
  useGetBookingsQuery,
  useGetUpcomingBookingsQuery,
  useRequestBookingMutation,
  useScheduleBookingMutation,
  useCompleteBookingMutation,
  useCancelBookingMutation,
} = humanCoachingApi;
